import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, PlusCircle, Trash2, Brain, Upload, Play, Target, TrendingUp, X, FileText } from 'lucide-react';

const SAMPLE_CSV = `location,monthly_sales,population,density,competitors,mobility_score,popularity_score,foot_traffic_daily_mid,avg_ticket
Sucursal Polanco,850000,140000,2400,4,92,88,4200,720
Sucursal Roma,620000,90000,1800,6,78,82,2800,650
Sucursal Interlomas,730000,110000,900,3,72,80,3200,700
Sucursal Del Valle,490000,180000,1500,8,65,71,2100,580
Sucursal Coyoacán,410000,120000,1300,5,60,74,1800,540
Sucursal Satélite,510000,95000,1100,4,68,69,2300,610`;

export default function MLPredictor() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMode, setNewMode] = useState(false);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [csvText, setCsvText] = useState(SAMPLE_CSV);
  const [creating, setCreating] = useState(false);

  const [activeDs, setActiveDs] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [manualFeatures, setManualFeatures] = useState({});
  const [prediction, setPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/sales-datasets');
      setDatasets(r.data || []);
    } catch { toast.error('Error cargando datasets'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get('/analyses').then(r => setAnalyses((r.data || []).filter(a => a.status === 'completado' || a.status === 'completado_con_advertencias'))).catch(() => {});
  }, []);

  const loadDs = async (id) => {
    try {
      const r = await api.get(`/sales-datasets/${id}`);
      setActiveDs(r.data);
      setPrediction(null);
      setSelectedAnalysis('');
      setManualFeatures({});
    } catch { toast.error('Error'); }
  };

  const createDs = async () => {
    if (!csvText.trim()) return toast.error('Pega un CSV');
    setCreating(true);
    try {
      const r = await api.post('/sales-datasets', { name: name || undefined, industry, csv_text: csvText });
      toast.success(`Dataset creado · R² = ${r.data.model?.r2 ?? '—'}`);
      setNewMode(false); setName(''); setIndustry(''); setCsvText(SAMPLE_CSV);
      load();
      loadDs(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando dataset'); }
    finally { setCreating(false); }
  };

  const deleteDs = async (id) => {
    if (!confirm('¿Eliminar dataset?')) return;
    try { await api.delete(`/sales-datasets/${id}`); load(); if (activeDs?.id === id) setActiveDs(null); } catch { toast.error('Error'); }
  };

  const doPredict = async () => {
    setPredicting(true);
    try {
      const body = {};
      const feats = {};
      Object.entries(manualFeatures).forEach(([k, v]) => {
        if (v !== '' && v != null && !isNaN(Number(v))) feats[k] = Number(v);
      });
      if (Object.keys(feats).length) body.features = feats;
      if (selectedAnalysis) body.analysis_id = selectedAnalysis;
      const r = await api.post(`/sales-datasets/${activeDs.id}/predict`, body);
      if (r.data.error) toast.error(r.data.error);
      else setPrediction(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error prediciendo'); }
    finally { setPredicting(false); }
  };

  const uploadFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result));
    reader.readAsText(f);
  };

  const model = activeDs?.model;
  const modelFeatures = model?.feature_names || [];

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="label-caps">Etapa 2 · Sprint 7</div>
      <div className="flex items-baseline justify-between mt-2 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Predictor de ventas</h1>
          <p className="text-slate-600 mt-1">Sube tu historial de ventas por sucursal y estima el rango de ventas mensuales para nuevas ubicaciones.</p>
        </div>
        <Button onClick={() => setNewMode(!newMode)} data-testid="toggle-new-dataset" className="bg-blue-900 hover:bg-blue-800">
          {newMode ? <><X size={14} className="mr-1" />Cerrar</> : <><PlusCircle size={14} className="mr-1" />Nuevo dataset</>}
        </Button>
      </div>

      {newMode && (
        <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="new-dataset-form">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="label-caps mb-1 block">Nombre</label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ds-name" placeholder="Historial retail 2023-2025" /></div>
            <div><label className="label-caps mb-1 block">Giro (opcional)</label><Input value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="ds-industry" placeholder="perfumería" /></div>
          </div>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <label className="label-caps">CSV de entrenamiento</label>
              <label className="text-xs text-blue-900 hover:underline cursor-pointer flex items-center gap-1">
                <Upload size={11} /> Subir archivo
                <input type="file" accept=".csv,text/csv" hidden onChange={uploadFile} data-testid="ds-file" />
              </label>
            </div>
            <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="ds-csv"
              className="w-full h-56 p-3 border border-slate-200 font-mono text-xs bg-slate-50" />
            <div className="text-xs text-slate-500 mt-1">Columna obligatoria: <code>monthly_sales</code>. Columnas opcionales (features): <code>population, density, competitors, mobility_score, popularity_score, foot_traffic_daily_mid, avg_ticket</code>, etc.</div>
          </div>
          <Button onClick={createDs} disabled={creating} data-testid="ds-submit" className="bg-blue-900 hover:bg-blue-800">
            {creating ? <><Loader2 size={14} className="mr-2 animate-spin" />Entrenando…</> : <><Brain size={14} className="mr-2" />Entrenar modelo</>}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Left: datasets list */}
        <div className="space-y-2">
          <div className="label-caps mb-2">Datasets ({datasets.length})</div>
          {loading ? <div className="text-slate-500 text-sm">Cargando…</div> :
            datasets.length === 0 ? <div className="border border-dashed border-slate-200 p-4 text-xs text-slate-400 text-center">Aún no hay datasets.</div> :
            datasets.map(d => (
              <button key={d.id} onClick={() => loadDs(d.id)} data-testid={`ds-${d.id}`}
                className={`w-full text-left bg-white border p-3 ${activeDs?.id === d.id ? 'border-blue-900' : 'border-slate-200 hover:border-slate-400'}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 text-sm truncate">{d.name}</div>
                  <button onClick={(e) => { e.stopPropagation(); deleteDs(d.id); }} className="text-slate-400 hover:text-red-700"><Trash2 size={12} /></button>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  R² <strong>{d.model?.r2 ?? '—'}</strong> · N <strong>{d.model?.n ?? '—'}</strong>
                  {d.industry && <> · {d.industry}</>}
                </div>
              </button>
            ))
          }
        </div>

        {/* Right: active dataset + predict */}
        <div>
          {!activeDs ? (
            <div className="border border-dashed border-slate-300 p-12 text-center text-slate-400">
              <Brain size={32} className="mx-auto mb-2 text-slate-300" />
              Selecciona un dataset o crea uno nuevo.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Model metrics */}
              <div className="bg-white border border-slate-200 p-5" data-testid="model-panel">
                <div className="label-caps">Modelo entrenado</div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">{activeDs.name}</h2>
                {model?.error ? (
                  <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-800 mt-3">{model.error}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-4 gap-3 mt-4">
                      <Kpi label="R² (ajuste)" value={model?.r2?.toFixed(2)} />
                      <Kpi label="Filas" value={model?.n} />
                      <Kpi label="Ventas media" value={model?.target_mean?.toLocaleString('es-MX', { maximumFractionDigits: 0 })} />
                      <Kpi label="Rango" value={`${(model?.target_min || 0).toLocaleString('es-MX')}–${(model?.target_max || 0).toLocaleString('es-MX')}`} small />
                    </div>
                    {model?.coefficients && (
                      <div className="mt-4">
                        <div className="label-caps mb-2">Coeficientes (por feature)</div>
                        <table className="w-full text-xs">
                          <tbody>
                            {Object.entries(model.coefficients).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([k, v]) => (
                              <tr key={k} className="border-b border-slate-100">
                                <td className="py-1 text-slate-700">{k}</td>
                                <td className="py-1 text-right font-mono font-semibold" style={{ color: v > 0 ? '#15803d' : '#b91c1c' }}>{v > 0 ? '+' : ''}{v.toFixed(3)}</td>
                              </tr>
                            ))}
                            <tr><td className="py-1 text-slate-500 italic">intercept</td><td className="py-1 text-right font-mono">{model?.intercept?.toFixed(2)}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                    {activeDs.parse_errors?.length > 0 && (
                      <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2">
                        Advertencias parse: {activeDs.parse_errors.join(', ')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Predict */}
              {!model?.error && (
                <div className="bg-white border border-slate-200 p-5" data-testid="predict-panel">
                  <div className="label-caps flex items-center gap-1"><Target size={12} />Predicción</div>
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-3">Ubicación objetivo</h3>

                  {analyses.length > 0 && (
                    <div className="mb-3">
                      <label className="text-xs text-slate-600 block mb-1">Auto-llenar desde un análisis existente</label>
                      <Select value={selectedAnalysis} onValueChange={setSelectedAnalysis}>
                        <SelectTrigger data-testid="predict-analysis-select"><SelectValue placeholder="— sin análisis, entrada manual —" /></SelectTrigger>
                        <SelectContent>
                          {analyses.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name} ({a.score_general})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    {modelFeatures.map(f => (
                      <div key={f}>
                        <label className="text-xs text-slate-600 block mb-1">{f}</label>
                        <Input type="number" step="0.1" value={manualFeatures[f] ?? ''}
                          onChange={(e) => setManualFeatures({ ...manualFeatures, [f]: e.target.value })}
                          data-testid={`feat-${f}`}
                          placeholder={`prom: ${model?.means?.[f]?.toFixed(1) ?? '—'}`} />
                      </div>
                    ))}
                  </div>

                  <Button onClick={doPredict} disabled={predicting} data-testid="predict-btn" className="bg-blue-900 hover:bg-blue-800">
                    {predicting ? <><Loader2 size={14} className="mr-2 animate-spin" />Prediciendo…</> : <><Play size={14} className="mr-2" />Predecir ventas</>}
                  </Button>

                  {prediction && (
                    <div className="mt-5 pt-5 border-t border-slate-100" data-testid="prediction-result">
                      <div className="label-caps">Ventas mensuales estimadas</div>
                      <div className="font-display text-4xl font-bold text-slate-900 mt-1">
                        ${prediction.monthly_sales.mid.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        Rango P25-P75: <strong>${prediction.monthly_sales.low.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong> – <strong>${prediction.monthly_sales.high.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        <div>Confianza: <strong className="text-slate-800">{(prediction.confidence * 100).toFixed(0)}%</strong></div>
                        <div>R² modelo: <strong className="text-slate-800">{prediction.model_r2}</strong></div>
                        <div>Filas entrenamiento: <strong className="text-slate-800">{prediction.training_n}</strong></div>
                        <div className="text-slate-400">Anual estimado: <strong>${prediction.annual_sales_mid.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong></div>
                      </div>
                      {prediction.features_imputed?.length > 0 && (
                        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
                          ⚠️ Features imputados (usó promedio del dataset): {prediction.features_imputed.join(', ')}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-slate-500 italic">
                        <FileText size={11} className="inline mr-1" />
                        Estimación estadística basada en regresión lineal regularizada sobre {prediction.training_n} filas. No es una proyección financiera exacta; valida contra tu experiencia comercial.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, small }) => (
  <div className="p-3 bg-slate-50 border border-slate-100">
    <div className="label-caps">{label}</div>
    <div className={`font-display font-bold text-slate-900 mt-1 ${small ? 'text-sm' : 'text-2xl'}`}>{value ?? '—'}</div>
  </div>
);
